import axios from "axios";
import { APP_PATH } from "../api/backendPath";

const API_BASE_URL = APP_PATH.labOrders;

// Create axios instance with auth token
const createAxiosInstance = () => {
  const token = sessionStorage.getItem("usr_1a2b3c");
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Get order details by ID
 * @param {string|number} orderId - Order ID
 * @returns {Promise} Order details
 */
export const getLabOrderDetails = async (orderId) => {
  console.log("=== getLabOrderDetails SERVICE CALL ===");
  console.log("orderId parameter:", orderId);
  console.log("API_BASE_URL:", API_BASE_URL);
  console.log("Full URL:", `${API_BASE_URL}/${orderId}`);

  try {
    const axiosInstance = createAxiosInstance();
    const token = sessionStorage.getItem("usr_1a2b3c");
    console.log("Token exists:", !!token);
    console.log(
      "Token (first 20 chars):",
      token ? token.substring(0, 20) + "..." : "NO TOKEN",
    );
    console.log("Base URL:", process.env.REACT_APP_API_URL);

    console.log("Making GET request to:", `${API_BASE_URL}/${orderId}`);
    const response = await axiosInstance.get(`${API_BASE_URL}/${orderId}`);

    console.log("Response received:");
    console.log("- Status:", response.status);
    console.log("- Data:", response.data);

    return response.data;
  } catch (error) {
    console.error("=== ERROR IN getLabOrderDetails SERVICE ===");
    console.error("Error:", error);
    console.error("Error response:", error.response);
    console.error("Error response status:", error.response?.status);
    console.error("Error response data:", error.response?.data);
    console.error("Error message:", error.message);
    throw error;
  }
};

/**
 * Generate invoice for an order
 * @param {string|number} orderId - Order ID
 * @returns {Promise} Invoice details
 */
export const generateLabOrderInvoice = async (orderId) => {
  try {
    const axiosInstance = createAxiosInstance();
    console.log("Generating invoice for order:", orderId);
    const response = await axiosInstance.post(
      `${API_BASE_URL}/${orderId}/invoice`,
    );
    console.log("Invoice response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating lab order invoice:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

/**
 * Add tests to an order
 * @param {string|number} orderId - Order ID
 * @param {Array} tests - Array of test objects
 * @returns {Promise} Response
 */
export const addLabOrderTests = async (orderId, tests, transportationFee) => {
  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.post(
      `${API_BASE_URL}/${orderId}/tests`,
      {
        tests,
        transportationFee: transportationFee || 0,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error adding tests to lab order:", error);
    throw error;
  }
};

/**
 * Update order status
 * @param {string|number} orderId - Order ID
 * @param {string} status - New status
 * @param {string} notes - Optional notes
 * @returns {Promise} Response
 */
export const updateLabOrderStatus = async (orderId, status, notes = "") => {
  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.put(
      `${API_BASE_URL}/${orderId}/status`,
      {
        status,
        notes,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating lab order status:", error);
    throw error;
  }
};

/**
 * Schedule appointment for an order
 * @param {string|number} orderId - Order ID
 * @param {string} scheduledDate - Scheduled date (YYYY-MM-DD)
 * @param {string} scheduledTime - Scheduled time (HH:MM)
 * @param {string} timezone - Timezone
 * @returns {Promise} Response
 */
export const scheduleLabOrderAppointment = async (
  orderId,
  scheduledDate,
  scheduledTime,
  timezone,
) => {
  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.put(
      `${API_BASE_URL}/${orderId}/schedule`,
      {
        scheduledDate,
        scheduledTime,
        timezone,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error scheduling lab order appointment:", error);
    throw error;
  }
};

/**
 * Assign technician to an order
 * @param {string|number} orderId - Order ID
 * @param {number} technicianId - Technician ID
 * @returns {Promise} Response
 */
export const assignLabOrderTechnician = async (orderId, technicianId) => {
  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.put(
      `${API_BASE_URL}/${orderId}/assign-technician`,
      {
        technicianId,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error assigning technician to lab order:", error);
    throw error;
  }
};

/**
 * Upload test results for an order
 * @param {string|number} orderId - Order ID
 * @param {string} fileUrl - File URL
 * @param {string} fileName - File name
 * @returns {Promise} Response
 */
export const uploadLabOrderTestResults = async (orderId, fileUrl, fileName) => {
  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.post(
      `${API_BASE_URL}/${orderId}/test-results`,
      {
        fileUrl,
        fileName,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading lab order test results:", error);
    throw error;
  }
};

/**
 * Send confirmation to patient with test details
 * @param {string|number} orderId - Order ID
 * @returns {Promise} Response
 */
export const sendLabOrderConfirmation = async (orderId) => {
  try {
    const axiosInstance = createAxiosInstance();
    console.log("Sending confirmation for order:", orderId);
    const response = await axiosInstance.post(
      `${API_BASE_URL}/${orderId}/send-confirmation`,
    );
    console.log("Send confirmation response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error sending lab order confirmation:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

/**
 * Get lab service fees configuration (admin fee and consultation fee)
 * @returns {Promise} Fees configuration
 */
export const getLabServiceFeesConfiguration = async () => {
  try {
    const axiosInstance = createAxiosInstance();
    console.log("Fetching lab service fees configuration");
    const response = await axiosInstance.get(
      `${API_BASE_URL}/fees/configuration`,
    );
    console.log("Lab service fees response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching lab service fees configuration:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

/**
 * Get available technicians for an order
 * @param {string|number} orderId - Order ID
 * @returns {Promise} List of available technicians
 */
export const getAvailableTechnicians = async (orderId) => {
  try {
    const axiosInstance = createAxiosInstance();
    console.log("Fetching available technicians for order:", orderId);
    const response = await axiosInstance.get(
      `${API_BASE_URL}/${orderId}/technicians/available`,
    );
    console.log("Available technicians response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching available technicians:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};
